import { useEffect, useId, useState } from 'react'
import { IconEye, IconEyeClosed } from '@tabler/icons-react'
import clsx from 'clsx'

import { Collapse } from 'shared/ui/Collapse'
import PasswordHint from 'shared/ui/form/PasswordHint'
import IconButton from 'shared/ui/IconButton/IconButton'
import { PASSWORD_RULES } from 'shared/utils/validators'

import TextField, { TextFieldProps } from './TextField'

import styles from './PasswordField.module.scss'

const PASSWORD_REQUIREMENTS_DESCRIPTION = `Wymagania dotyczące hasła: ${PASSWORD_RULES.map(
  ({ label }) => label.toLowerCase()
).join(', ')}.`

const REQUIREMENTS_ANNOUNCEMENT_DELAY_MS = 1000

const getRequirementsAnnouncement = (password: string) => {
  const metCount = PASSWORD_RULES.filter(({ pattern }) =>
    pattern.test(password)
  ).length

  return metCount === PASSWORD_RULES.length
    ? 'Hasło spełnia wszystkie wymagania'
    : `Spełniono ${metCount} z ${PASSWORD_RULES.length} wymagań hasła`
}

interface PasswordFieldProps extends Omit<TextFieldProps, 'type'> {
  withPasswordHint?: boolean
  valueToPasswordHint?: string
}

const PasswordField = ({
  icons,
  withPasswordHint,
  marginBottom = true,
  valueToPasswordHint = '',
  onFocus,
  onBlur,
  ...props
}: PasswordFieldProps) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [focused, setFocused] = useState(false)
  const generatedId = useId()
  // Explicit id is passed down to TextField, so aria-controls points at the rendered input
  const inputId = props.id || `password-${generatedId}`
  const requirementsId = `${inputId}-requirements`
  const [requirementsAnnouncement, setRequirementsAnnouncement] = useState('')

  // Announces only after a typing pause; an unchanged text is not re-read by the live region
  useEffect(() => {
    if (!withPasswordHint || !focused || !valueToPasswordHint) return

    const timeout = setTimeout(() => {
      setRequirementsAnnouncement(
        getRequirementsAnnouncement(valueToPasswordHint)
      )
    }, REQUIREMENTS_ANNOUNCEMENT_DELAY_MS)

    return () => clearTimeout(timeout)
  }, [withPasswordHint, focused, valueToPasswordHint])

  const handlePasswordVisibilityChange = () => {
    setIsPasswordVisible(prev => !prev)
  }

  const handleFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFocused(true)
    onFocus?.(e)
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFocused(false)
    onBlur?.(e)
  }

  return (
    <div
      className={clsx(styles.wrapper, {
        [styles.noMarginBottom]: !marginBottom
      })}
    >
      <TextField
        type={isPasswordVisible ? 'text' : 'password'}
        onFocus={handleFocus}
        onBlur={handleBlur}
        marginBottom={false}
        icons={{
          end: (
            <IconButton
              size='medium'
              variant='transparent'
              onClick={handlePasswordVisibilityChange}
              type='button'
              aria-label='Pokaż hasło'
              aria-pressed={isPasswordVisible}
              aria-controls={inputId}
            >
              {isPasswordVisible ? <IconEyeClosed /> : <IconEye />}
            </IconButton>
          ),
          ...icons
        }}
        {...props}
        id={inputId}
        inputProps={{
          ...props.inputProps,
          'aria-describedby':
            clsx(
              props.inputProps?.['aria-describedby'],
              withPasswordHint && requirementsId
            ) || undefined
        }}
      />
      {withPasswordHint && (
        <>
          {/* The visible list is hidden until focus (Collapse), so the requirements are described separately */}
          <span id={requirementsId} className='visually-hidden'>
            {PASSWORD_REQUIREMENTS_DESCRIPTION}
          </span>
          <span className='visually-hidden' aria-live='polite'>
            {requirementsAnnouncement}
          </span>
          <Collapse open={focused}>
            <PasswordHint password={valueToPasswordHint || ''} />
          </Collapse>
        </>
      )}
    </div>
  )
}

export default PasswordField
